import React from "react";
import songsAPI from "restApi/songsApi";
import { fetchSeen } from "restApi/seenApi";
import { setBadgeSource } from "utils/appBadge";
import useSeenTime from "hooks/useSeenTime";
import { LAST_SEEN_PROPOSALS_AT_KEY } from "../CONSTANTS";

// A couple of minutes strikes a balance between the badge feeling live
// and not hammering the API - mirrors useUpcomingEvents' own interval.
const POLL_INTERVAL_MS = 2 * 60 * 1000;

// Polls pending song proposals app-wide and tracks when the user last
// visited /song-proposals - on any of their devices, kept on their account
// (hooks/useSeenTime) - so the Proposals tab can badge however many are new
// since then. Checks again whenever the app comes back into view, so looking
// at the page on one device clears the dot on the others. Deliberately silent
// otherwise - no push notification for a new proposal (too frequent an event
// to justify interrupting someone's phone for), just this in-app badge and,
// mirroring it, the installed app's home-screen icon badge (see
// utils/appBadge - unlike events, that badge is set from here directly
// rather than from a server push).
export default function useSongProposals(enabled) {
  const [songs, setSongs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Seen on another device since this device last knew: release proposals'
  // claim on the icon badge here too.
  const onAdvanced = React.useCallback(() => setBadgeSource("proposals", false), []);
  const { lastSeenAt, sync, markSeen: recordSeen } = useSeenTime({
    what: "proposals",
    storageKey: LAST_SEEN_PROPOSALS_AT_KEY,
    onAdvanced,
  });

  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;

    const poll = () => {
      songsAPI
        .get("/api/songs/?status=pending")
        .then((res) => {
          if (cancelled) return;
          setSongs(res.data);
          setLoading(false);
        })
        .catch(() => {
          if (!cancelled) setLoading(false);
        });
      // Independent of the list: a slow or failed answer here must not hold the
      // proposals back (the dot just waits for it).
      fetchSeen()
        .then((seen) => {
          if (!cancelled) sync(seen);
        })
        .catch(() => {});
    };

    const pollWhenVisible = () => {
      if (document.visibilityState === "visible") poll();
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", pollWhenVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", pollWhenVisible);
    };
  }, [enabled, sync]);

  const newCount = lastSeenAt
    ? songs.filter((s) => new Date(s.proposed_date) > new Date(lastSeenAt))
        .length
    : 0;

  // Only ever *claims* the shared app-icon badge here, never releases
  // it - the coordinator (utils/appBadge) has no visibility into a
  // badge set by a push notification (events, or a song-approval push -
  // both set it straight from the service worker, with no page-side
  // "true" registered for it), so a passive "nothing new" result from
  // this poll firing setBadgeSource(..., false) - which it would do on
  // every mount, before the user has looked at anything - would wipe
  // out a genuine unseen push badge that has nothing to do with
  // proposals. Only markSeen() (an actual visit to the page) releases
  // this source.
  React.useEffect(() => {
    if (newCount > 0) {
      setBadgeSource("proposals", true);
    }
  }, [newCount]);

  // Marks the list as seen right now (clears the in-app tab badge, here and -
  // at their next check - on the user's other devices, and releases
  // proposals' claim on the shared app-icon badge) - anything proposed after
  // this point is what counts as "new" going forward.
  const markSeen = React.useCallback(() => {
    recordSeen();
    setBadgeSource("proposals", false);
  }, [recordSeen]);

  return { songs, loading, newCount, markSeen };
}
